import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMediumEntryScreen } from './hooks/useMediumEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
      />
    </label>
  );
}

export default function MediumEntryScreen() {
  const { state, actions } = useMediumEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-12">
      {/* Top Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Education</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <Link to="/masters/medium/list" className="hover:text-primary transition-colors">
              Medium Registry
            </Link>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">{state.isEditMode ? 'Edit' : 'Create'}</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Language / Medium' : 'Create Language / Medium'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Define languages and instruction mediums for course configurations and admissions.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/medium/list"
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-5 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-highest cursor-pointer"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isSaving ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{state.isEditMode ? 'Update Medium' : 'Save Medium'}</span>
            )}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="mt-3 text-xs font-medium text-on-surface-variant">Loading medium details...</span>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-lg font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    label="Medium Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. English, Hindi, Marathi, Gujarati, Semi-English"
                  />
                </div>

                <div className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Operational Status
                  </span>
                  <div className="flex items-center rounded-xl bg-surface-container-low p-1">
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('active')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'active'
                          ? 'bg-surface-container-lowest text-primary shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('inactive')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'inactive'
                          ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Archived
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <aside className="col-span-12 space-y-6 lg:col-span-4">
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-6 shadow-sm">
              <h3 className="text-sm font-bold text-on-surface">Configuration Completion</h3>
              <div className="mt-4 flex items-center gap-4">
                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-base font-black text-primary">{state.completionPercent}%</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-on-surface">Setup Progress</p>
                  <p className="text-[11px] text-on-surface-variant mt-0.5">Fill medium name to complete configuration.</p>
                </div>
              </div>
            </article>
          </aside>
        </section>
      )}
    </div>
  );
}
